import { supabase } from '@/integrations/supabase/client';
import { getFreePlanLimits } from './freePlanLimitsService';

export type FuncionarioModule = 'chat_messages' | 'documents';

export interface FuncionarioUsage {
  chat_messages: number;
  documents: number;
}

const ZERO: FuncionarioUsage = { chat_messages: 0, documents: 0 };

interface CacheEntry {
  userId: string;
  usage: FuncionarioUsage;
  timestamp: number;
}

const CACHE_TTL = 30_000;
let cache: CacheEntry | null = null;

export function invalidateFuncionarioQuotaCache() {
  cache = null;
}

export async function getFuncionarioLimits(): Promise<FuncionarioUsage> {
  const limits = await getFreePlanLimits();
  return {
    chat_messages: limits.funcionario_chat_messages,
    documents: limits.funcionario_documents,
  };
}

export async function getFuncionarioUsage(userId: string): Promise<FuncionarioUsage> {
  if (!userId) return { ...ZERO };

  if (cache && cache.userId === userId && Date.now() - cache.timestamp < CACHE_TTL) {
    return cache.usage;
  }

  try {
    const { data, error } = await (supabase.rpc as any)('get_funcionario_free_usage', {
      p_user_id: userId,
    });

    if (error) {
      console.warn('[funcionarioQuota] RPC error:', error);
      return { ...ZERO };
    }

    const usage: FuncionarioUsage = {
      chat_messages: Number((data as any)?.chat_messages ?? 0),
      documents: Number((data as any)?.documents ?? 0),
    };

    cache = { userId, usage, timestamp: Date.now() };
    return usage;
  } catch (err) {
    console.warn('[funcionarioQuota] unexpected error:', err);
    return { ...ZERO };
  }
}

/** El plan free de funcionarios aplica límites; admins/super_admins/betatesters quedan exentos. */
export function isFreeFuncionario(plan: string | undefined | null, profile: any): boolean {
  if (!plan || plan !== 'free') return false;
  if (profile?.is_betatester) return false;
  if (profile?.role === 'admin' || profile?.role === 'super_admin') return false;
  return true;
}

export interface QuotaCheckResult {
  allowed: boolean;
  used: number;
  limit: number;
  remaining: number;
}

export async function checkFuncionarioQuota(
  userId: string,
  plan: string | undefined | null,
  profile: any,
  module: FuncionarioModule
): Promise<QuotaCheckResult> {
  const limits = await getFuncionarioLimits();
  const limit = limits[module];

  if (!isFreeFuncionario(plan, profile)) {
    return { allowed: true, used: 0, limit: Infinity, remaining: Infinity };
  }

  const usage = await getFuncionarioUsage(userId);
  const used = usage[module] ?? 0;
  const remaining = Math.max(0, limit - used);
  return {
    allowed: used < limit,
    used,
    limit,
    remaining,
  };
}
